/**
 * Territory is a reporting dimension keyed by zip: the branch service area is the
 * hard intake boundary, territories partition it, zips are the atoms. Nothing here
 * feeds assignment.
 */
import type { BranchConfig, RosterEntry, SandboxState } from "../state";

export type ZipStatus = "open" | "closed" | "outOfArea";

export function territoryForZip(branch: BranchConfig, zip?: string): string | undefined {
  if (!zip) return undefined;
  const five = zip.slice(0, 5);
  return branch.territories.find((t) => t.zips.includes(five))?.name;
}

export function isInServiceArea(branch: BranchConfig, zip?: string): boolean {
  return !!zip && branch.serviceAreaZips.includes(zip.slice(0, 5));
}

/** Closed zips after sandbox toggles are applied. */
export function effectiveClosedZips(branch: BranchConfig, sandbox: SandboxState | null): string[] {
  const closed = new Set(branch.closedZips);
  for (const [zip, status] of Object.entries(sandbox?.zipToggles ?? {})) {
    if (status === "closed") closed.add(zip);
    else closed.delete(zip);
  }
  return [...closed].sort();
}

export function zipStatus(branch: BranchConfig, zip: string | undefined, sandbox: SandboxState | null = null): ZipStatus {
  if (!isInServiceArea(branch, zip)) return "outOfArea";
  return effectiveClosedZips(branch, sandbox).includes(zip!.slice(0, 5)) ? "closed" : "open";
}

export function territoryNames(branch: BranchConfig): string[] {
  return branch.territories.map((t) => t.name);
}

/**
 * Where a clinician's supply lands: the territories they own, or the home-zip
 * territory for a per diem (who owns nothing). Never empty.
 */
export function supplyTerritoriesFor(entry: Pick<RosterEntry, "territories" | "homeZip" | "territory">, branch: BranchConfig): string[] {
  if (entry.territories.length) return entry.territories;
  return [territoryForZip(branch, entry.homeZip) ?? entry.territory ?? "Unassigned"];
}

/** Drive estimate between a clinician's territory and a patient zip when no real drive exists. */
export function estimateDriveMinutes(clinicianTerritory: string, patientTerritory: string | undefined): number {
  if (!patientTerritory) return 30;
  if (clinicianTerritory === patientTerritory) return 15;
  const adjacent: Record<string, string[]> = { North: ["East", "West"], East: ["North", "South"], South: ["East", "West"], West: ["North", "South"] };
  return adjacent[clinicianTerritory]?.includes(patientTerritory) ? 28 : 42;
}
